import { Link } from '@tanstack/react-router';

import { Eyebrow, Tag } from '@/components/editorial';
import { AppImage } from '@/components/layout/app-image';
import type { PublicWritingRecord } from '@/content/schemas';

import { getCategoryLabel } from '../categories';
import { TableOfContents } from './table-of-contents';
import { WritingDiscussion } from './writing-discussion';

type WritingDetailPageProps = {
  article: PublicWritingRecord;
  bodyHtml: string;
  related: PublicWritingRecord[];
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function WritingDetailPage({
  article,
  bodyHtml,
  related,
}: WritingDetailPageProps) {
  const categoryLabel = getCategoryLabel(article.category);

  return (
    <article className="w-full">
      <div className="border-border w-full border-b">
        <div className="container max-w-3xl px-6 py-16 md:py-24">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <Link
              to="/blog"
              className="text-muted-foreground hover:text-brand font-mono text-xs transition-colors"
            >
              ← Writing
            </Link>
            <span className="text-border">·</span>
            <Tag variant="outline" href={`/blog/category/${article.category}`}>
              {categoryLabel}
            </Tag>
          </div>

          <Eyebrow className="mb-4">{formatDate(article.publishedAt)}</Eyebrow>

          <h1 className="font-display text-ink mb-6 text-4xl font-semibold leading-tight md:text-5xl">
            {article.title}
          </h1>

          <p className="text-muted-foreground mb-8 text-lg leading-relaxed">
            {article.description}
          </p>

          <div className="text-muted-foreground flex flex-wrap items-center gap-4 font-mono text-xs">
            <span>{formatDate(article.publishedAt)}</span>
            <span>·</span>
            <span>{article.readingMinutes} min read</span>
            {article.tags.length > 0 && (
              <>
                <span>·</span>
                <div className="flex flex-wrap gap-1.5">
                  {article.tags.map((tag) => (
                    <Tag key={tag} variant="muted">
                      {tag}
                    </Tag>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="w-full">
        <div className="container max-w-4xl px-6">
          <div className="relative aspect-video w-full overflow-hidden rounded-b-xl">
            <AppImage
              src={article.hero.src}
              alt={article.hero.alt}
              fill
              className="object-cover"
            />
          </div>
        </div>
      </div>

      <div className="container max-w-5xl px-6 py-12 md:py-16">
        <div className="lg:grid lg:grid-cols-[1fr_240px] lg:gap-16">
          <div
            className="prose-custom min-w-0"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
          <TableOfContents headings={article.headings} />
        </div>

        <hr className="border-border/60 my-12" />

        <div className="mb-12">
          <h2 className="font-display text-ink mb-6 text-2xl font-semibold">
            Discussion
          </h2>
          <WritingDiscussion enabled={article.discussionEnabled} />
        </div>

        {related.length > 0 && (
          <div className="mt-12">
            <h2 className="font-display text-ink mb-6 text-2xl font-semibold">
              More from {categoryLabel}
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
              {related.map((entry) => (
                <Link
                  key={entry.id}
                  to="/blog/$slug"
                  params={{ slug: entry.slug }}
                  className="border-border hover:border-brand group flex flex-col gap-2 rounded-xl border p-4 transition-colors"
                >
                  <p className="text-muted-foreground font-mono text-xs">
                    {formatDate(entry.publishedAt)} · {entry.readingMinutes} min
                  </p>
                  <h3 className="font-display text-ink group-hover:text-brand text-base font-semibold leading-snug transition-colors">
                    {entry.title}
                  </h3>
                  <p className="text-muted-foreground line-clamp-2 text-sm leading-relaxed">
                    {entry.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12">
          <Link
            to="/blog"
            className="text-muted-foreground hover:text-brand font-mono text-sm transition-colors"
          >
            ← All posts
          </Link>
        </div>
      </div>
    </article>
  );
}
